import { theme } from '@theme';
import { formatStringAsPerLocale } from '@utils/HelperFunctions';
import { FC, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { DealType } from '../../../../enums/DealType';
import { useDealType } from '../../../../hooks/UseDealType';
import { useOutsideClickDetection } from '../../../../hooks/UseOutsideClickDetection';
import { setIsPortfolioResetPaginator } from '../../../../store/portfolio/PortfolioActions';
import { FilterItemResource } from '../../../../types/FilterTypes';
import {
  Icon,
  NumberBadge,
  OptionItemInput,
  PortfolioBadge,
  PortfolioDropdownItemLabel,
  PortfolioDropdownOptions,
  PortfolioOptionItem,
  Typography,
} from '../../../style';

type PortfolioDropdownProps = {
  dealType: string;
  options: FilterItemResource[];
  isBulkOperation: boolean;
  onPortfolioSelection: (dealType: string) => void;
};

const PortfolioDropdown: FC<PortfolioDropdownProps> = ({
  dealType,
  options,
  isBulkOperation,
  onPortfolioSelection,
}) => {
  const wrapperRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [selectedValue, setSelectedValue] = useState<FilterItemResource>(
    options.find((x) => x.key.toLowerCase() === dealType?.toLowerCase()) || {
      key: DealType.AllDeals,
      label: DealType.AllDeals,
    },
  );
  const dispatch = useDispatch();
  const { getColorCode } = useDealType();
  useOutsideClickDetection(wrapperRef, setIsVisible);

  useEffect(() => {
    if (isVisible) {
      setIsVisible(!isVisible);
    }
  }, [dealType]);

  const handleDropdownWrapperClick = (): void => {
    setIsVisible(!isVisible);
    if (!isVisible) {
    }
  };

  const isSelected = (item: FilterItemResource): boolean => {
    return selectedValue?.key === item.key;
  };

  const onItemChange = (item: FilterItemResource): void => {
    setSelectedValue(item);
    dispatch(setIsPortfolioResetPaginator(true));
    if (item.key === DealType.AllDeals) {
      onPortfolioSelection('all');
    } else {
      onPortfolioSelection(item.key.toLowerCase());
    }
  };
  const generateOptions = (): JSX.Element[] => {
    return options.map((item) => {
      if (item.count !== null && item.count !== undefined) {
        return (
          <PortfolioOptionItem
            selected={item.key === selectedValue?.key}
            key={item.key}
            borderBottom={true}
            onClick={() => onItemChange(item)}
          >
            <span>
              <OptionItemInput
                type='radio'
                checked={isSelected(item)}
                onChange={() => onItemChange(item)}
              />
              <PortfolioDropdownItemLabel>{item.label}</PortfolioDropdownItemLabel>
            </span>
            <span className='filter-right'>
              {!!item.key && (
                <NumberBadge>{formatStringAsPerLocale(item.count?.toString())}</NumberBadge>
              )}
            </span>
          </PortfolioOptionItem>
        );
      }
    });
  };

  useEffect(() => {
    setSelectedValue(
      options.find((x) => x.key.toLowerCase() === dealType?.toLowerCase()) || {
        key: DealType.AllDeals,
        label: DealType.AllDeals,
      },
    );
  }, [options]);

  return (
    <PortfolioDropdownWrapper data-testid='portfolio-dropdown' ref={wrapperRef}>
      <div className='heap-filter-item'>
        <span data-testid='portfolio-dropdown-header' onClick={handleDropdownWrapperClick}>
          <div>
            <Typography
              theme={theme}
              size={theme.typography.h3}
              fontWeight={theme.typography.fontWeights.bold}
            >
              Portfolio
            </Typography>
            {selectedValue?.key !== DealType.AllDeals.toString() ? (
              <PortfolioBadge backgroundColor={getColorCode(selectedValue.key)}>
                {selectedValue?.label}
              </PortfolioBadge>
            ) : (
              <Typography
                theme={theme}
                size={theme.typography.h3}
                paddingBottom={7}
                fontWeight={theme.typography.fontWeights.bold}
                marginRight={5}
                className='portfolio-all-text'
              >
                : All
              </Typography>
            )}
            <IconWrapper className='line-separator'>
              <Icon
                type='chevron-down'
                size={theme.typography.h6}
                color={theme.palette.basicGray}
              />
            </IconWrapper>
          </div>
        </span>
        {isVisible && (
          <PortfolioDropdownOptions top={50} left={10} isBulkOperation={isBulkOperation}>
            {generateOptions()}
          </PortfolioDropdownOptions>
        )}
      </div>
    </PortfolioDropdownWrapper>
  );
};

export default PortfolioDropdown;

const PortfolioDropdownWrapper = styled.span`
  display: flex;
  padding: 11px 12px;
  line-height: 16px;
  & div:first-child {
    display: flex;
    justify-content: space-between;
  }
  &:hover {
    cursor: pointer;
  }
`;

const IconWrapper = styled.div`
  margin-top: 1px;
`;
